import { useEffect } from "react";
import { Redirect, Route, Switch, useLocation, useSearch } from "wouter";
import { BootScreen } from "./components/BootScreen";
import { Notice } from "./components/ui";
import { inFrame } from "./lib/api";
import { Shell } from "./components/Shell";
import { useMe } from "./lib/queries";
import { useRealtime } from "./lib/realtime";
import { useTheme } from "./lib/theme";
import { Home } from "./pages/Home";
import { Privacy, Terms } from "./pages/Legal";
import { Login } from "./pages/Login";
import { NoCompanies } from "./pages/NoCompanies";
import { Settings } from "./pages/settings/Settings";
import { ToolPage } from "./pages/ToolPage";
import { ToolWindows } from "./components/ToolWindows";
import { useLastPlace } from "./lib/lastPlace";

/*
 * Routes. Opened from ServiceM8's phone app (lib/api.ts phoneView) the same pages show in a
 * simpler frame: no top bar or Settings (Shell).
 */
export function App() {
    const { data: me, isLoading, error, refetch, isFetching } = useMe();
    useTheme(me?.user.theme);
    const realtime = useRealtime(Boolean(me), me?.companies.map((c) => c.id).join(",") ?? "");
    const [path] = useLocation();

    // Public, signed in or not (and without waiting to find out).
    if (path === "/privacy") return <Privacy />;
    if (path === "/terms") return <Terms />;
    if (isLoading) return <BootScreen />;
    // Not known (the server restarting for an update, or offline): said, with Retry. Only a refusal means signed out.
    if (me === undefined) return <CantReach error={error} busy={isFetching} onRetry={refetch} />;
    // Signed out inside ServiceM8: signing in by email can't work there (its window blocks the cookie), so it's opened again.
    if (!me && inFrame) return <SessionEnded />;
    if (!me) {
        return (
            <Switch>
                <Route path="/login" component={Login} />
                <Route>
                    <ToLogin />
                </Route>
            </Switch>
        );
    }

    const first = me.companies[0];
    const home = first ? `/c/${first.id}` : "/";
    // Redirects get distinct keys: wouter's <Redirect> only navigates on mount, and React would
    // otherwise reuse one Redirect instance across these routes, so a second hop never fires.
    return (
        <Switch>
            <Route path="/login">
                <AfterLogin home={home} />
            </Route>
            <Route path="/c/:companyId" nest>
                {(params) => (
                    <Shell me={me} companyId={params.companyId} realtime={realtime}>
                        <Switch>
                            <Route path="/" component={Home} />
                            <Route path="/settings/:section?" component={Settings} />
                            <Route path="/t/:toolId/*?" component={ToolPage} />
                        </Switch>
                        <ToolWindows />
                        <LastPlace userId={me.user.id} companyId={params.companyId} />
                    </Shell>
                )}
            </Route>
            <Route>{first ? <Redirect key="home" to={home} replace /> : <NoCompanies me={me} />}</Route>
        </Switch>
    );
}

/** Keeps where they are in this business, and goes back there when ServiceM8 opens the app afresh (lib/lastPlace.ts). */
function LastPlace(props: Readonly<{ userId: string; companyId: string }>) {
    useLastPlace(props.userId, props.companyId);
    return null;
}

/** Remember where they were going (e.g. a link from ServiceM8) so signing in takes them there. */
function ToLogin() {
    const [path] = useLocation();
    const search = useSearch();
    if (path === "/") return <Redirect to="/login" replace />;
    const query = search ? "?" + search : "";
    return <Redirect to={`/login?next=${encodeURIComponent(path + query)}`} replace />;
}

function AfterLogin({ home }: Readonly<{ home: string }>) {
    const next = new URLSearchParams(useSearch()).get("next");
    // Only paths within this app (not "//other.site", nor "/\other.site", which browsers read the same).
    const to = next?.startsWith("/") && new URL(next, location.origin).origin === location.origin ? next : home;
    return <Redirect key="login" to={to} replace />;
}

/** Who's signed in couldn't be found out (the server restarting for an update, or no connection): tried again every few seconds. */
function CantReach(props: Readonly<{ error: Error | null; busy: boolean; /** Stable (its query's refetch), so its timer isn't restarted each render. */ onRetry: () => unknown }>) {
    const { onRetry } = props;
    useEffect(() => {
        const timer = setInterval(onRetry, 5000);
        return () => clearInterval(timer);
    }, [onRetry]);
    return (
        <main className="grid min-h-full place-items-center bg-canvas p-4">
            <div className="w-full max-w-md">
                <Notice
                    tone="warning"
                    title="Can't reach Outback Workbench."
                    actions={
                        <button type="button" className="btn-secondary" disabled={props.busy} onClick={() => void onRetry()}>
                            Retry
                        </button>
                    }
                >
                    It may be updating, or you may be offline: trying again by itself. {props.error ? <span className="mt-0.5 block text-xs opacity-80">{props.error.message}</span> : null}
                </Notice>
            </div>
        </main>
    );
}

/** Signed out inside ServiceM8 (its session ended): opened from ServiceM8 again, which signs in by itself. */
function SessionEnded() {
    return (
        <main className="grid min-h-full place-items-center bg-canvas p-4">
            <div className="w-full max-w-md">
                <Notice title="Your session has ended.">Close this and open Outback Workbench from ServiceM8 again.</Notice>
            </div>
        </main>
    );
}
